// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Link, Loading, Popover, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {
  ResponsiveType,
  useCallback,
  useDebouncedCallback,
  useDrawer,
  useNavigationDOM,
  useMemo,
  usePagination,
  usePopover,
  useResponsive,
  useState,
  useUrlFilters,
  useToast,
} from '@supermove/hooks';
import {
  InboundWebhookDeliveryStatus,
  InboundWebhookEndpoint,
  InboundWebhookEndpointRequestsOptions,
  InboundWebhookRequest,
  LeadProviderEndpoint,
  UserModel,
} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';
import {Datetime, URL} from '@supermove/utils';

// App
import Badge from '@shared/design/components/Badge';
import Breadcrumbs from '@shared/design/components/Breadcrumbs';
import Button from '@shared/design/components/Button';
import SecondaryButton from '@shared/design/components/Button/SecondaryButton';
import Checkbox from '@shared/design/components/Checkbox';
import FieldInput from '@shared/design/components/Field/FieldInput';
import MultiDropdownInputComponent from '@shared/design/components/MultiDropdownInput';
import PaginationBar from '@shared/design/components/Pagination/PaginationBar';
import SearchBar from '@shared/design/components/SearchBar';
import Table from '@shared/design/components/Table';
import Tabs, {TabWithIndexType} from '@shared/design/components/Tabs';
import Toast from '@shared/design/components/Toast';
import ErrorToast from '@shared/design/components/Toast/ErrorToast';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';
import PopoverFilter from '@shared/modules/App/components/PopoverFilter';
import CompanySettingsPage from 'modules/Organization/Settings/Company/components/CompanySettingsPage';
import LeadProviderRequestModal from 'modules/Organization/Settings/Company/components/LeadProvider/LeadProviderRequestModal';
import {LeadProviderRequestStatus} from 'modules/Organization/Settings/Company/components/LeadProvider/LeadProviderRequestStatus';
import useRetryLeadProviderRequestsMutation from 'modules/Organization/Settings/Company/components/LeadProvider/useRetryLeadProviderRequestsMutation';

const HeaderContainer = Styled.View<{
  isResponsive?: boolean;
  responsive: ResponsiveType;
}>`
  padding-horizontal: ${({isResponsive, responsive}) => (isResponsive && !responsive.desktop ? 16 : 0)}px;
`;

const HeaderDescription = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.secondary};
  max-width: 700px;
`;

const TabsContainer = Styled.View`
  border-bottom-width: 1px;
  border-color: ${colors.gray.border};
  width: 100%;
`;

const HeaderActions = Styled.View<{responsive: ResponsiveType}>`
  flex-direction: ${({responsive}) => (responsive.desktop ? 'row' : 'column')};
  align-items: center;
`;

const HeaderActionsColumn = Styled.View<{responsive: ResponsiveType; justifyContent: string}>`
  flex-direction: row;
  justify-content: ${({justifyContent}) => justifyContent};
  align-items: center;
  width: ${({responsive}) => (responsive.desktop ? 'auto' : '100%')};
`;

const ActionText = Styled.Text<{color: string}>`
  ${Typography.Responsive.Label}
  color: ${({color}) => color};
`;

const LinkButton = Styled.ButtonV2`
`;

const LinkText = Styled.Text`
  ${Typography.Responsive.Link}
`;

const OrganizationSettingsCompanyLeadProvidersLogsPage = () => {
  const responsive = useResponsive();
  const {navigator, params} = useNavigationDOM();
  const page = _.toNumber(params.page) || 1;

  const urlFilters = useUrlFilters<PopoverFilters & {search: string}>({
    getRoute: () => `/settings/company/lead-providers/${params.uuid}/logs`,
    filterKeys: ['status', 'search'],
  });
  const filters = urlFilters.getFilters();
  const [selectedRequests, setSelectedRequests] = useState(new Set<string>());

  const {loading, data, refetch} = useQuery<Response>(
    OrganizationSettingsCompanyLeadProvidersLogsPage.query,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        inboundWebhookEndpointUuid: params.uuid,
        pagination: {
          page,
          resultsPerPage: 50,
        },
        options: {
          status: filters.status,
          search: filters.search,
        },
      },
    },
  );

  const pagination = usePagination({
    currentPage: page,
    paginationMetadata:
      data?.leadProviderEndpoint.inboundWebhookEndpoint.requests.paginationMetadata || {},
    onChangePage: (nextPage: number) => {
      navigator.push(URL.getUrlFromVariables(navigator.location.pathname, {page: nextPage}));
    },
  });
  const onChangeTextSearch = useDebouncedCallback(
    (value: string) =>
      urlFilters.handleUpdate({
        search: value,
      }),
    500,
  );

  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Successfully submitted requests.',
  });
  const errorToast = useToast({
    ToastComponent: ErrorToast,
    message: 'Something went wrong. Please refresh the page and try again.',
  });
  const retryLeadProviderRequestsMutation = useRetryLeadProviderRequestsMutation({
    onSuccess: () => {
      setSelectedRequests(new Set());
      successToast.handleToast();
      refetch();
    },
    onError: (error) => {
      console.error({error});
      errorToast.handleToast();
    },
  });

  const Header = () => {
    return (
      <HeaderContainer isResponsive responsive={responsive}>
        <Space height={32} />
        <Breadcrumbs
          breadcrumbs={[
            {
              name: 'Lead Providers',
              onPress: () => navigator.push('/settings/company/lead-providers'),
            },
            {
              name: data!.leadProviderEndpoint.inboundWebhookEndpoint.name,
            },
          ]}
          mobileOptions={{showPreviousScreenOnly: true}}
        />
        <Space height={12} />
        <HeaderDescription responsive={responsive}>
          {'View and access request logs for this provider.'}
        </HeaderDescription>
        <Space height={12} />
        <HeaderActions responsive={responsive}>
          <HeaderActionsColumn responsive={responsive} justifyContent={'flex-start'}>
            <SearchBar
              onChangeText={onChangeTextSearch}
              onEnterPress={onChangeTextSearch}
              placeholder='Search by ID'
              style={{width: '348px'}}
              defaultValue={filters.search}
            />
            {responsive.desktop ? <Space width={12} /> : <Space flex={1} />}
            <Filter urlFilters={urlFilters} handleApply={urlFilters.handleUpdate} />
          </HeaderActionsColumn>
          {responsive.desktop ? <Space flex={1} /> : <Space height={16} />}
          <HeaderActionsColumn responsive={responsive} justifyContent={'flex-end'}>
            <ActionText color={colors.gray.tertiary} responsive={responsive}>
              {data!.leadProviderEndpoint.inboundWebhookEndpoint.status === 'active'
                ? `${selectedRequests.size} requests selected`
                : 'Lead provider must be in Active status to retry requests'}
            </ActionText>
            {responsive.desktop ? <Space width={12} /> : <Space flex={1} />}
            <Button
              text={'Retry requests'}
              isDisabled={selectedRequests.size === 0}
              isSubmitting={retryLeadProviderRequestsMutation.submitting}
              onPress={() => {
                retryLeadProviderRequestsMutation.handleSubmit({
                  variables: {
                    retryLeadProviderRequestsForm: {
                      inboundWebhookRequestIds: Array.from(selectedRequests).map((id) =>
                        parseInt(id, 10),
                      ),
                    },
                  },
                });
              }}
              isResponsive
            />
          </HeaderActionsColumn>
        </HeaderActions>
        <Space height={12} />
        <TabsContainer>
          <Space height={8} />
          <Tabs
            tabs={[
              {
                label: 'Leads',
                count: data!.leadProviderEndpoint.summary.leads,
              },
              {
                label: 'Logs',
                count:
                  data!.leadProviderEndpoint.inboundWebhookEndpoint.requests.paginationMetadata
                    .totalResults,
              },
            ]}
            handlePressTab={({label, index}: TabWithIndexType) => {
              switch (label) {
                case 'Leads':
                  navigator.push(`/settings/company/lead-providers/${params.uuid}/leads`);
                  return;
                case 'Logs':
                  return; // Do nothing
              }
            }}
            activeTabIndex={1}
            isSpacedTabs
          />
        </TabsContainer>
      </HeaderContainer>
    );
  };

  return (
    <Loading loading={loading} as={PageLoadingIndicator}>
      {() => (
        <CompanySettingsPage HeaderComponent={Header} title={''} description={''}>
          <Content
            viewer={data!.viewer}
            endpoint={data!.leadProviderEndpoint.inboundWebhookEndpoint}
            selectedRequests={selectedRequests}
            setSelectedRequests={setSelectedRequests}
          />
          <Space height={16} />
          <PaginationBar pagination={pagination} />
        </CompanySettingsPage>
      )}
    </Loading>
  );
};

const Content = ({
  viewer,
  endpoint,
  selectedRequests,
  setSelectedRequests,
}: {
  viewer: UserModel;
  endpoint: InboundWebhookEndpoint;
  selectedRequests: Set<string>;
  setSelectedRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
}) => {
  const responsive = useResponsive();
  const {viewingOrganization} = viewer;

  const requestDetailsModal = useDrawer({
    name: 'Lead Provider Logs Request Details Modal',
  });
  const [requestDetailsModalOption, setRequestDetailsModalOption] =
    useState<InboundWebhookRequest | null>(null);

  const handleToggle = useCallback(
    (request: InboundWebhookRequest) => {
      if (selectedRequests.has(request.id)) {
        setSelectedRequests((requests) => {
          const next = new Set(requests);
          next.delete(request.id);
          return next;
        });
      } else {
        setSelectedRequests((requests) => new Set(requests).add(request.id));
      }
    },
    [selectedRequests, setSelectedRequests],
  );
  const isActionsEnabled = !!endpoint.inboundWebhookDestinations && endpoint.status === 'active';

  const copiedToast = useToast({
    ToastComponent: Toast,
    message: `Copied to clipboard.`,
  });

  const columnDefinitions = [
    {
      width: 48,
      headerComponent: () => (
        <Checkbox
          isChecked={
            selectedRequests.size === endpoint.requests.results.length &&
            endpoint.requests.results.length > 0
          }
          isDisabled={!isActionsEnabled || endpoint.requests.results.length === 0}
          handleToggle={() => {
            if (selectedRequests.size === endpoint.requests.results.length) {
              setSelectedRequests(new Set());
            } else {
              setSelectedRequests(new Set(endpoint.requests.results.map((request) => request.id)));
            }
          }}
        />
      ),
      headerLabel: responsive.desktop ? undefined : 'Retry request',
      cellComponent: (request: InboundWebhookRequest) => (
        <RequestCheckbox
          request={request}
          selectedRequests={selectedRequests}
          isDisabled={!isActionsEnabled}
          handleToggle={handleToggle}
        />
      ),
    },
    {
      headerLabel: 'Received At',
      width: 160,
      cellText: (request: InboundWebhookRequest) =>
        Datetime.toDisplayTime(
          Datetime.fromDatetime(request.createdAt),
          Datetime.DISPLAY_DATETIME,
          viewingOrganization.timezone,
        ),
    },
    {
      headerLabel: 'Status',
      width: 100,
      cellComponent: (request: InboundWebhookRequest) => {
        const status = LeadProviderRequestStatus.fromRequest(request)!;
        return <Badge label={status.name} color={status.color} />;
      },
    },
    {
      headerLabel: 'Request ID',
      width: 320,
      cellComponent: (request: InboundWebhookRequest) =>
        responsive.desktop ? (
          <LinkButton
            onPress={() => {
              setRequestDetailsModalOption(request);
              requestDetailsModal.handleOpen();
            }}
          >
            <LinkText>
              <pre style={{fontSize: '13px'}}>{request.uuid}</pre>
            </LinkText>
          </LinkButton>
        ) : (
          <pre style={{fontSize: '13px'}}>{request.uuid}</pre>
        ),
      mobileOptions: {
        isInHeader: true,
        rank: 0,
      },
    },
    {
      headerLabel: 'Project',
      flex: 1,
      cellComponent: ({mostRecentDelivery}: InboundWebhookRequest) =>
        mostRecentDelivery?.project && (
          <Link to={`/projects/${mostRecentDelivery.project.uuid}`} target={'_blank'}>
            <LinkText>{`Project ${mostRecentDelivery.project.identifier}`}</LinkText>
          </Link>
        ),
      secondary: {
        headerLabel: 'Delivered At',
        cellText: ({mostRecentDelivery}: InboundWebhookRequest) =>
          mostRecentDelivery?.deliveredAt &&
          Datetime.toDisplayTime(
            Datetime.fromDatetime(mostRecentDelivery.deliveredAt),
            Datetime.DISPLAY_DATETIME,
            viewingOrganization.timezone,
          ),
      },
    },
    {
      flex: 0.5,
      cellStyle: {alignItems: 'center'},
      actions: (request: InboundWebhookRequest) => [
        {
          text: 'View Details',
          onPress: () => {
            setRequestDetailsModalOption(request);
            requestDetailsModal.handleOpen();
          },
        },
        {
          text: 'Copy Link',
          onPress: () => {
            const searchParams = new URLSearchParams({search: request.uuid});
            navigator.clipboard.writeText(
              `${window.location.origin}${window.location.pathname}?${searchParams}`,
            );
            copiedToast.handleToast();
          },
        },
      ],
    },
  ];

  return (
    <React.Fragment>
      <Table
        items={endpoint.requests.results}
        itemKey={'id'}
        columnDefinitions={columnDefinitions}
        hasBorder
        emptyStateText={'No requests for this provider yet.'}
      />
      {requestDetailsModalOption && (
        <LeadProviderRequestModal
          inboundWebhookRequestUuid={requestDetailsModalOption.uuid}
          isOpen={requestDetailsModal.isOpen}
          handleClose={requestDetailsModal.handleClose}
        />
      )}
    </React.Fragment>
  );
};

const RequestCheckbox = ({
  request,
  selectedRequests,
  isDisabled,
  handleToggle,
}: {
  request: InboundWebhookRequest;
  selectedRequests: Set<string>;
  isDisabled: boolean;
  handleToggle: (request: InboundWebhookRequest) => void;
}) => {
  const checked = useMemo(() => selectedRequests.has(request.id), [request, selectedRequests]);
  return (
    <Checkbox
      isChecked={checked}
      isDisabled={isDisabled}
      handleToggle={() => {
        handleToggle(request);
      }}
      style={{marginBottom: 4}}
    />
  );
};

const Filter = ({
  urlFilters,
  handleApply,
}: {
  urlFilters: ReturnType<typeof useUrlFilters<PopoverFilters>>;
  handleApply: (props: PopoverFilters) => void;
}) => {
  const popover = usePopover({
    name: 'Company Settings Lead Provider Logs Filter',
  });
  const filterCount = urlFilters.getFilterCount({
    filterKeys: ['status'],
  });

  return (
    <React.Fragment>
      <Popover.Content innerRef={popover.ref}>
        <SecondaryButton
          text={`Filters ${filterCount ? `(${filterCount})` : ''}`}
          onPress={popover.handleOpen}
          iconLeft={Icon.Filter}
          isWidthOfContainer
          isResponsive
        />
      </Popover.Content>
      {popover.isOpen && (
        <FilterPopover
          urlStatus={urlFilters.get('status')}
          popover={popover}
          handleApply={handleApply}
        />
      )}
    </React.Fragment>
  );
};

const FilterPopover = ({
  popover,
  urlStatus,
  handleApply,
}: {
  popover: ReturnType<typeof usePopover>;
  urlStatus: PopoverFilters['status'];
  handleApply: (props: PopoverFilters) => void;
}) => {
  const responsive = useResponsive();

  const [status, setStatus] = useState(urlStatus);
  const filtersCount = status?.length > 0 ? 1 : 0;

  return (
    <PopoverFilter
      activeFiltersCount={filtersCount}
      popover={popover}
      responsive={responsive}
      handleApply={() => {
        handleApply({
          status,
        });
        popover.handleClose();
      }}
      handleClear={() => {
        setStatus([]);
        handleApply({
          status: [],
        });
        popover.handleClose();
      }}
    >
      <React.Fragment>
        <FieldInput
          index={0}
          component={MultiDropdownInputComponent}
          label={'Status'}
          input={{
            options: Object.values(InboundWebhookDeliveryStatus).map((value) => ({
              label: LeadProviderRequestStatus.pretty[value].name,
              value,
            })),
            name: 'Status',
            value: status,
            setFieldValue: (name: string, value: PopoverFilters['status']) => setStatus(value),
            placeholder: 'Select status',
            style: {flex: 1},
            isResponsive: true,
          }}
        />
        <Space height={16} />
      </React.Fragment>
    </PopoverFilter>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsCompanyLeadProvidersLogsPage.query = gql`
  ${usePagination.fragment}
  
  query OrganizationSettingsCompanyLeadProvidersLogsPage($inboundWebhookEndpointUuid: String!, $pagination: PaginationInput!, $options: InboundWebhookEndpointRequestsOptions!){
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        timezone
      }
    }
    leadProviderEndpoint: leadProviderEndpoint(inboundWebhookEndpointUuid: $inboundWebhookEndpointUuid) {
      id
      summary {
        leads
      }
      inboundWebhookEndpoint {
        id
        uuid
        status
        name
        requests(pagination: $pagination, options: $options) {
            paginationMetadata {
                totalResults
                totalPages
                ...usePagination
            }
          results {
            id
            createdAt
            uuid
            mostRecentDelivery {
              id
              createdAt
              deliveredAt
              uuid
              status
              isExpired
              project {
                id
                uuid
                identifier
              }
            }
          }
        }
        inboundWebhookDestinations {
          id
        }
      }
    }
  }
`;

interface Response {
  viewer: UserModel;
  leadProviderEndpoint: LeadProviderEndpoint;
}

type PopoverFilters = Omit<InboundWebhookEndpointRequestsOptions, 'search'>;

export default OrganizationSettingsCompanyLeadProvidersLogsPage;
